import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Slider } from "../components/ui/slider"
import {Button} from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";

import { 
  ChevronLeft, 
  ChevronRight, 
  Download, 
  Save, 
  User, 
  Mail, 
  Users, 
  Heart, 
  Home, 
  Briefcase, 
  GraduationCap, 
  CheckCircle,
  Circle,
  Target,
  Plus,
  Trash2
} from 'lucide-react';

const Consumers = () => {
  const steps = [
    { name: 'Personal Information', icon: User },
    { name: 'Contact Details', icon: Mail },
    { name: 'Demographics', icon: Users },
    { name: 'Health Information', icon: Heart },
    { name: 'Living Situation', icon: Home },
    { name: 'Employment Income', icon: Briefcase },
    { name: 'Education', icon: GraduationCap },
    { name: 'Goals', icon: Target },
    { name: 'Review', icon: CheckCircle }
  ];

  const initialFormData = {
    personalInfo: {
      FirstName: '',
      LastName: '',
      MiddleName__c: '',
      Suffix__c: '',
      Birthdate: '',
      SSN__c: '',
      Preferred_Pronouns__c: '',
      Gender__c: '',
      Race__c: ''
    },
    contactDetails: {
      MailingStreet: '',
      MailingStreet2: '',
      MailingCity: '',
      MailingState: '',
      MailingPostalCode: '',
      County__c: '',
      MobilePhone: '',
      HomePhone: '',
      Phone: '', // Work Phone
      Email: '',
      Text_Messages__c: false
    },
    demographics: {
      Consumer_Type__c: '',
      Veteran_Status__c: '',
      Registered_Voter__c: false,
      Emergency_Plan__c: false,
      Identification__c: ''
    },
    healthInfo: {
      Primary_Disability_Category__c: '',
      Primary_Disability__c: '',
      Primary_Disability_Text__c: '',
      Secondary_Disability_Category__c: '',
      Secondary_Disability__c: '',
      Secondary_Disability_Text__c: '',
      Communications_Needs__c: '',
      Health_Insurance_Primary__c: '',
      Health_Insurance_Secondary__c: '',
      Insurance_Plans__c: '',
      Medicaid_Plan__c: ''
    },
    livingSituation: {
      Number_in_Household__c: '',
      Head_of_Household__c: '',
      Marital_Status__c: '',
      Number_of_Children__c: '',
      Emergency_Contact_Information__c: '',
      Living_Situation_on_Entry__c: '',
      Current_Living_Situation__c: '',
      Living_Situation_Accessible__c: '',
      Living_Situation_Comments__c: '',
      Relocation_Successful_from_Institution__c: '',
      Relocation_Prevented__c: '',
      Transportation_Method__c: '',
      Transportation_Adequate__c: '',
      Transportation_Notes__c: '',
      Communication_Style__c: ''
    },
    employmentIncome: {
      Currently_Employed__c: '',
      Employer__c: '',
      Job_Title__c: '',
      Work_Phone__c: '',
      Employment_Goals__c: '',
      Employment_Comments__c: '',
      Employment_Status_on_Entry__c: '',
      Employment_Start__c: '',
      Employment_End__c: '',
      Reason_for_Leaving__c: '',
      Income_Source__c: '',
      PRIV__c: '',
      SSDI__c: '',
      SSI__c: '',
      VA__c: '',
      SSA__c: '',
      Other__c: '',
      Other_Type__c: '',
      Income_Level__c: ''
    },
    education: {
      Highest_Education_Completed__c: '',
      Education__c: '',
      Current_Education_Goals__c: '',
      Educational_Comments__c: '',
      Communication_Notes__c: ''
    },
    goals: []
  };

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);

  // Load data from localStorage on initial render
  useEffect(() => {
    const savedData = localStorage.getItem('consumerWizardData');
    if (savedData) {
      setFormData(JSON.parse(savedData));
    }
    const savedStep = localStorage.getItem('consumerWizardStep');
    if (savedStep) {
      setCurrentStep(parseInt(savedStep));
    }
  }, []);

  // Save data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('consumerWizardData', JSON.stringify(formData));
    localStorage.setItem('consumerWizardStep', currentStep.toString());
  }, [formData, currentStep]);

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const downloadJson = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(formData, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", "consumer_data.json");
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const renderPersonalInfo = () => (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-100">
        <div className="flex items-center gap-2 mb-2">
          <User className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-blue-900">Basic Information</h3>
        </div>
        <p className="text-sm text-blue-700">Please provide the consumer's personal details</p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="FirstName" className="text-sm font-medium">First Name *</Label>
          <Input
            id="FirstName"
            value={formData.personalInfo.FirstName}
            onChange={(e) => handleInputChange('personalInfo', 'FirstName', e.target.value)}
            className="transition-all focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="LastName" className="text-sm font-medium">Last Name *</Label>
          <Input
            id="LastName"
            value={formData.personalInfo.LastName}
            onChange={(e) => handleInputChange('personalInfo', 'LastName', e.target.value)}
            className="transition-all focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="MiddleName__c" className="text-sm font-medium">Middle Name</Label>
          <Input
            id="MiddleName__c"
            value={formData.personalInfo.MiddleName__c}
            onChange={(e) => handleInputChange('personalInfo', 'MiddleName__c', e.target.value)}
            className="transition-all focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="Suffix__c" className="text-sm font-medium">Suffix</Label>
          <Input
            id="Suffix__c"
            value={formData.personalInfo.Suffix__c}
            onChange={(e) => handleInputChange('personalInfo', 'Suffix__c', e.target.value)}
            placeholder="Jr., Sr., III"
            className="transition-all focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="Birthdate" className="text-sm font-medium">Birth Date *</Label>
          <Input
            id="Birthdate"
            type="date"
            value={formData.personalInfo.Birthdate}
            onChange={(e) => handleInputChange('personalInfo', 'Birthdate', e.target.value)}
            className="transition-all focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="SSN__c" className="text-sm font-medium">SSN</Label>
          <Input
            id="SSN__c"
            type="password"
            value={formData.personalInfo.SSN__c}
            onChange={(e) => handleInputChange('personalInfo', 'SSN__c', e.target.value)}
            placeholder="***-**-****"
            className="transition-all focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="Preferred_Pronouns__c" className="text-sm font-medium">Preferred Pronouns</Label>
          <Input
            id="Preferred_Pronouns__c"
            value={formData.personalInfo.Preferred_Pronouns__c}
            onChange={(e) => handleInputChange('personalInfo', 'Preferred_Pronouns__c', e.target.value)}
            placeholder="e.g., he/him, she/her, they/them"
            className="transition-all focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="Gender__c" className="text-sm font-medium">Gender</Label>
          <Select
            id="Gender__c"
            value={formData.personalInfo.Gender__c}
            onValueChange={(value) => handleInputChange('personalInfo', 'Gender__c', value)}
          >
            <SelectTrigger className="transition-all focus:ring-2 focus:ring-blue-500">
              <SelectValue placeholder="Select gender" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
              <SelectItem value="non-binary">Non-binary</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="Race__c" className="text-sm font-medium">Race/Ethnicity</Label>
        <Select
          id="Race__c"
          value={formData.personalInfo.Race__c}
          onValueChange={(value) => handleInputChange('personalInfo', 'Race__c', value)}
        >
          <SelectTrigger className="transition-all focus:ring-2 focus:ring-blue-500">
            <SelectValue placeholder="Select race/ethnicity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="american-indian">American Indian or Alaska Native</SelectItem>
            <SelectItem value="asian">Asian</SelectItem>
            <SelectItem value="black">Black or African American</SelectItem>
            <SelectItem value="hispanic">Hispanic or Latino</SelectItem>
            <SelectItem value="pacific-islander">Native Hawaiian or Pacific Islander</SelectItem>
            <SelectItem value="white">White</SelectItem>
            <SelectItem value="multiple">Two or More Races</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  const renderContactDetails = () => (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-100">
        <div className="flex items-center gap-2 mb-2">
          <Mail className="w-5 h-5 text-green-600" />
          <h3 className="font-semibold text-green-900">Contact Information</h3>
        </div>
        <p className="text-sm text-green-700">How can we reach this consumer?</p>
      </div>

      <div className="space-y-4">
        <h4 className="font-medium text-sm text-gray-700 flex items-center gap-2">
          <Home className="w-4 h-4" />
          Mailing Address
        </h4>
        <div className="space-y-2">
          <Label htmlFor="MailingStreet" className="text-sm font-medium">Street Address *</Label>
          <Input
            id="MailingStreet"
            value={formData.contactDetails.MailingStreet}
            onChange={(e) => handleInputChange('contactDetails', 'MailingStreet', e.target.value)}
            placeholder="Street address"
            className="transition-all focus:ring-2 focus:ring-green-500"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="MailingStreet2" className="text-sm font-medium">Address Line 2</Label>
          <Input
            id="MailingStreet2"
            value={formData.contactDetails.MailingStreet2}
            onChange={(e) => handleInputChange('contactDetails', 'MailingStreet2', e.target.value)}
            placeholder="Apt, Suite, Unit, etc."
            className="transition-all focus:ring-2 focus:ring-green-500"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="MailingCity" className="text-sm font-medium">City *</Label>
            <Input
              id="MailingCity"
              value={formData.contactDetails.MailingCity}
              onChange={(e) => handleInputChange('contactDetails', 'MailingCity', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="MailingState" className="text-sm font-medium">State *</Label>
            <Input
              id="MailingState"
              value={formData.contactDetails.MailingState}
              onChange={(e) => handleInputChange('contactDetails', 'MailingState', e.target.value)}
              placeholder="CA"
              maxLength={2}
              className="transition-all focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="MailingPostalCode" className="text-sm font-medium">Postal Code *</Label>
            <Input
              id="MailingPostalCode"
              value={formData.contactDetails.MailingPostalCode}
              onChange={(e) => handleInputChange('contactDetails', 'MailingPostalCode', e.target.value)}
              placeholder="12345"
              className="transition-all focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="County__c" className="text-sm font-medium">County</Label>
            <Input
              id="County__c"
              value={formData.contactDetails.County__c}
              onChange={(e) => handleInputChange('contactDetails', 'County__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
      </div>

      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium text-sm text-gray-700 flex items-center gap-2">
          <Mail className="w-4 h-4" />
          Phone & Email
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="MobilePhone" className="text-sm font-medium">Mobile Phone</Label>
            <Input
              id="MobilePhone"
              type="tel"
              value={formData.contactDetails.MobilePhone}
              onChange={(e) => handleInputChange('contactDetails', 'MobilePhone', e.target.value)}
              placeholder="(555) 123-4567"
              className="transition-all focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="HomePhone" className="text-sm font-medium">Home Phone</Label>
            <Input
              id="HomePhone"
              type="tel"
              value={formData.contactDetails.HomePhone}
              onChange={(e) => handleInputChange('contactDetails', 'HomePhone', e.target.value)}
              placeholder="(555) 123-4567"
              className="transition-all focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="Phone" className="text-sm font-medium">Work Phone</Label>
            <Input
              id="Phone"
              type="tel"
              value={formData.contactDetails.Phone}
              onChange={(e) => handleInputChange('contactDetails', 'Phone', e.target.value)}
              placeholder="(555) 123-4567"
              className="transition-all focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="Email" className="text-sm font-medium">Email Address</Label>
            <Input
              id="Email"
              type="email"
              value={formData.contactDetails.Email}
              onChange={(e) => handleInputChange('contactDetails', 'Email', e.target.value)}
              placeholder="email@example.com"
              className="transition-all focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
        <div className="flex items-center space-x-2 pt-2">
          <Checkbox
            id="Text_Messages__c"
            checked={formData.contactDetails.Text_Messages__c}
            onCheckedChange={(checked) => handleInputChange('contactDetails', 'Text_Messages__c', checked)}
          />
          <Label htmlFor="Text_Messages__c" className="text-sm font-medium cursor-pointer">
            Opt-in to receive text messages
          </Label>
        </div>
      </div>
    </div>
  );

  const renderDemographics = () => (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-100">
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold text-purple-900">Demographics</h3>
        </div>
        <p className="text-sm text-purple-700">Additional demographic information</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="Consumer_Type__c" className="text-sm font-medium">Consumer Type</Label>
        <Select
          id="Consumer_Type__c"
          value={formData.demographics.Consumer_Type__c}
          onValueChange={(value) => handleInputChange('demographics', 'Consumer_Type__c', value)}
        >
          <SelectTrigger className="transition-all focus:ring-2 focus:ring-purple-500">
            <SelectValue placeholder="Select consumer type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="new">New Consumer</SelectItem>
            <SelectItem value="returning">Returning Consumer</SelectItem>
            <SelectItem value="transfer">Transfer</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="Veteran_Status__c" className="text-sm font-medium">Veteran Status</Label>
        <Select
          id="Veteran_Status__c"
          value={formData.demographics.Veteran_Status__c}
          onValueChange={(value) => handleInputChange('demographics', 'Veteran_Status__c', value)}
        >
          <SelectTrigger className="transition-all focus:ring-2 focus:ring-purple-500">
            <SelectValue placeholder="Select veteran status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="veteran">Veteran</SelectItem>
            <SelectItem value="active-duty">Active Duty</SelectItem>
            <SelectItem value="not-veteran">Not a Veteran</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-4 p-4 bg-purple-50 rounded-lg border border-purple-100">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="Registered_Voter__c"
            checked={formData.demographics.Registered_Voter__c}
            onCheckedChange={(checked) => handleInputChange('demographics', 'Registered_Voter__c', checked)}
          />
          <Label htmlFor="Registered_Voter__c" className="text-sm font-medium cursor-pointer">
            Registered Voter
          </Label>
        </div>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="Emergency_Plan__c"
            checked={formData.demographics.Emergency_Plan__c}
            onCheckedChange={(checked) => handleInputChange('demographics', 'Emergency_Plan__c', checked)}
          />
          <Label htmlFor="Emergency_Plan__c" className="text-sm font-medium cursor-pointer">
            Has Emergency Plan
          </Label>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="Identification__c" className="text-sm font-medium">Identification</Label>
        <Input
          id="Identification__c"
          value={formData.demographics.Identification__c}
          onChange={(e) => handleInputChange('demographics', 'Identification__c', e.target.value)}
          placeholder="ID number or reference"
          className="transition-all focus:ring-2 focus:ring-purple-500"
        />
      </div>
    </div>
  );

  const renderHealthInfo = () => (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border border-red-100">
        <div className="flex items-center gap-2 mb-2">
          <Heart className="w-5 h-5 text-red-600" />
          <h3 className="font-semibold text-red-900">Health Information</h3>
        </div>
        <p className="text-sm text-red-700">Disability and health insurance details</p>
      </div>

      <div className="space-y-4">
        <h4 className="font-medium text-sm text-gray-700">Primary Disability</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="Primary_Disability_Category__c" className="text-sm font-medium">Category</Label>
            <Select
              id="Primary_Disability_Category__c"
              value={formData.healthInfo.Primary_Disability_Category__c}
              onValueChange={(value) => handleInputChange('healthInfo', 'Primary_Disability_Category__c', value)}
            >
              <SelectTrigger className="transition-all focus:ring-2 focus:ring-red-500">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cognitive">Cognitive</SelectItem>
                <SelectItem value="mental">Mental/Emotional</SelectItem>
                <SelectItem value="physical">Physical</SelectItem>
                <SelectItem value="sensory">Sensory</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="Primary_Disability__c" className="text-sm font-medium">Specific Disability</Label>
            <Input
              id="Primary_Disability__c"
              value={formData.healthInfo.Primary_Disability__c}
              onChange={(e) => handleInputChange('healthInfo', 'Primary_Disability__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-red-500"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="Primary_Disability_Text__c" className="text-sm font-medium">Description</Label>
          <Textarea
            id="Primary_Disability_Text__c"
            value={formData.healthInfo.Primary_Disability_Text__c}
            onChange={(e) => handleInputChange('healthInfo', 'Primary_Disability_Text__c', e.target.value)}
            rows={3}
            className="transition-all focus:ring-2 focus:ring-red-500"
          />
        </div>
      </div>

      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium text-sm text-gray-700">Secondary Disability (Optional)</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="Secondary_Disability_Category__c" className="text-sm font-medium">Category</Label>
            <Select
              id="Secondary_Disability_Category__c"
              value={formData.healthInfo.Secondary_Disability_Category__c}
              onValueChange={(value) => handleInputChange('healthInfo', 'Secondary_Disability_Category__c', value)}
            >
              <SelectTrigger className="transition-all focus:ring-2 focus:ring-red-500">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cognitive">Cognitive</SelectItem>
                <SelectItem value="mental">Mental/Emotional</SelectItem>
                <SelectItem value="physical">Physical</SelectItem>
                <SelectItem value="sensory">Sensory</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="Secondary_Disability__c" className="text-sm font-medium">Specific Disability</Label>
            <Input
              id="Secondary_Disability__c"
              value={formData.healthInfo.Secondary_Disability__c}
              onChange={(e) => handleInputChange('healthInfo', 'Secondary_Disability__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-red-500"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="Secondary_Disability_Text__c" className="text-sm font-medium">Description</Label>
          <Textarea
            id="Secondary_Disability_Text__c"
            value={formData.healthInfo.Secondary_Disability_Text__c}
            onChange={(e) => handleInputChange('healthInfo', 'Secondary_Disability_Text__c', e.target.value)}
            rows={3}
            className="transition-all focus:ring-2 focus:ring-red-500"
          />
        </div>
      </div>

      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium text-sm text-gray-700">Communication & Insurance</h4>
        <div className="space-y-2">
          <Label htmlFor="Communications_Needs__c" className="text-sm font-medium">Communications Needs</Label>
          <Select
            id="Communications_Needs__c"
            value={formData.healthInfo.Communications_Needs__c}
            onValueChange={(value) => handleInputChange('healthInfo', 'Communications_Needs__c', value)}
          >
            <SelectTrigger className="transition-all focus:ring-2 focus:ring-red-500">
              <SelectValue placeholder="Select communications needs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="asl">American Sign Language</SelectItem>
              <SelectItem value="braille">Braille</SelectItem>
              <SelectItem value="large-print">Large Print</SelectItem>
              <SelectItem value="none">None</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="Health_Insurance_Primary__c" className="text-sm font-medium">Primary Insurance</Label>
            <Input
              id="Health_Insurance_Primary__c"
              value={formData.healthInfo.Health_Insurance_Primary__c}
              onChange={(e) => handleInputChange('healthInfo', 'Health_Insurance_Primary__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-red-500"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="Health_Insurance_Secondary__c" className="text-sm font-medium">Secondary Insurance</Label>
            <Input
              id="Health_Insurance_Secondary__c"
              value={formData.healthInfo.Health_Insurance_Secondary__c}
              onChange={(e) => handleInputChange('healthInfo', 'Health_Insurance_Secondary__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-red-500"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="Insurance_Plans__c" className="text-sm font-medium">Insurance Plans</Label>
          <Input
            id="Insurance_Plans__c"
            value={formData.healthInfo.Insurance_Plans__c}
            onChange={(e) => handleInputChange('healthInfo', 'Insurance_Plans__c', e.target.value)}
            className="transition-all focus:ring-2 focus:ring-red-500"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="Medicaid_Plan__c" className="text-sm font-medium">Medicaid Plan</Label>
          <Input
            id="Medicaid_Plan__c"
            value={formData.healthInfo.Medicaid_Plan__c}
            onChange={(e) => handleInputChange('healthInfo', 'Medicaid_Plan__c', e.target.value)}
            className="transition-all focus:ring-2 focus:ring-red-500"
          />
        </div>
      </div>
    </div>
  );

  const renderLivingSituation = () => (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="bg-gradient-to-r from-green-50 to-teal-50 p-4 rounded-lg border border-green-100">
        <div className="flex items-center gap-2 mb-2">
          <Home className="w-5 h-5 text-green-600" />
          <h3 className="font-semibold text-green-900">Living Situation</h3>
        </div>
        <p className="text-sm text-green-700">Please provide information about the consumer's living situation</p>
      </div>

      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium text-sm text-gray-700">Household Information</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="Number_in_Household__c" className="text-sm font-medium">Number in Household</Label>
            <Input
              id="Number_in_Household__c"
              type="number"
              value={formData.livingSituation.Number_in_Household__c}
              onChange={(e) => handleInputChange('livingSituation', 'Number_in_Household__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="Head_of_Household__c" className="text-sm font-medium">Head of Household</Label>
            <Input
              id="Head_of_Household__c"
              value={formData.livingSituation.Head_of_Household__c}
              onChange={(e) => handleInputChange('livingSituation', 'Head_of_Household__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="Marital_Status__c" className="text-sm font-medium">Marital Status</Label>
            <Input
              id="Marital_Status__c"
              value={formData.livingSituation.Marital_Status__c}
              onChange={(e) => handleInputChange('livingSituation', 'Marital_Status__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="Number_of_Children__c" className="text-sm font-medium">Number of Children</Label>
            <Input
              id="Number_of_Children__c"
              type="number"
              value={formData.livingSituation.Number_of_Children__c}
              onChange={(e) => handleInputChange('livingSituation', 'Number_of_Children__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="Emergency_Contact_Information__c" className="text-sm font-medium">Emergency Contact Information</Label>
          <Input
            id="Emergency_Contact_Information__c"
            value={formData.livingSituation.Emergency_Contact_Information__c}
            onChange={(e) => handleInputChange('livingSituation', 'Emergency_Contact_Information__c', e.target.value)}
            className="transition-all focus:ring-2 focus:ring-green-500"
          />
        </div>
      </div>

      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium text-sm text-gray-700">Living Status</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="Living_Situation_on_Entry__c" className="text-sm font-medium">Living Situation on Entry</Label>
            <Input
              id="Living_Situation_on_Entry__c"
              value={formData.livingSituation.Living_Situation_on_Entry__c}
              onChange={(e) => handleInputChange('livingSituation', 'Living_Situation_on_Entry__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="Current_Living_Situation__c" className="text-sm font-medium">Current Living Situation</Label>
            <Input
              id="Current_Living_Situation__c"
              value={formData.livingSituation.Current_Living_Situation__c}
              onChange={(e) => handleInputChange('livingSituation', 'Current_Living_Situation__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="Living_Situation_Accessible__c" className="text-sm font-medium">Living Situation Accessible</Label>
          <Input
            id="Living_Situation_Accessible__c"
            value={formData.livingSituation.Living_Situation_Accessible__c}
            onChange={(e) => handleInputChange('livingSituation', 'Living_Situation_Accessible__c', e.target.value)}
            className="transition-all focus:ring-2 focus:ring-green-500"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="Living_Situation_Comments__c" className="text-sm font-medium">Living Situation Comments</Label>
          <Textarea
            id="Living_Situation_Comments__c"
            value={formData.livingSituation.Living_Situation_Comments__c}
            onChange={(e) => handleInputChange('livingSituation', 'Living_Situation_Comments__c', e.target.value)}
            rows={3}
            className="transition-all focus:ring-2 focus:ring-green-500"
          />
        </div>
      </div>

      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium text-sm text-gray-700">Relocation</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="Relocation_Successful_from_Institution__c" className="text-sm font-medium">Relocation Successful from Institution</Label>
            <Input
              id="Relocation_Successful_from_Institution__c"
              value={formData.livingSituation.Relocation_Successful_from_Institution__c}
              onChange={(e) => handleInputChange('livingSituation', 'Relocation_Successful_from_Institution__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="Relocation_Prevented__c" className="text-sm font-medium">Relocation Prevented</Label>
            <Input
              id="Relocation_Prevented__c"
              value={formData.livingSituation.Relocation_Prevented__c}
              onChange={(e) => handleInputChange('livingSituation', 'Relocation_Prevented__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
      </div>

      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium text-sm text-gray-700">Transportation</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="Transportation_Method__c" className="text-sm font-medium">Transportation Method</Label>
            <Input
              id="Transportation_Method__c"
              value={formData.livingSituation.Transportation_Method__c}
              onChange={(e) => handleInputChange('livingSituation', 'Transportation_Method__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="Transportation_Adequate__c" className="text-sm font-medium">Transportation Adequate?</Label>
            <Input
              id="Transportation_Adequate__c"
              value={formData.livingSituation.Transportation_Adequate__c}
              onChange={(e) => handleInputChange('livingSituation', 'Transportation_Adequate__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="Transportation_Notes__c" className="text-sm font-medium">Transportation Notes</Label>
          <Textarea
            id="Transportation_Notes__c"
            value={formData.livingSituation.Transportation_Notes__c}
            onChange={(e) => handleInputChange('livingSituation', 'Transportation_Notes__c', e.target.value)}
            rows={3}
            className="transition-all focus:ring-2 focus:ring-green-500"
          />
        </div>
      </div>

      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium text-sm text-gray-700">Communication</h4>
        <div className="space-y-2">
          <Label htmlFor="Communication_Style__c" className="text-sm font-medium">Communication Style</Label>
          <Input
            id="Communication_Style__c"
            value={formData.livingSituation.Communication_Style__c}
            onChange={(e) => handleInputChange('livingSituation', 'Communication_Style__c', e.target.value)}
            className="transition-all focus:ring-2 focus:ring-green-500"
          />
        </div>
      </div>
    </div>
  );

  const renderEmploymentIncome = () => (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-100">
        <div className="flex items-center gap-2 mb-2">
          <Briefcase className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold text-purple-900">Employment & Income</h3>
        </div>
        <p className="text-sm text-purple-700">Please provide information about employment and income</p>
      </div>

      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium text-sm text-gray-700">Employment Information</h4>
        <div className="space-y-2">
          <Label htmlFor="Currently_Employed__c" className="text-sm font-medium">Currently Employed</Label>
          <Input
            id="Currently_Employed__c"
            value={formData.employmentIncome.Currently_Employed__c}
            onChange={(e) => handleInputChange('employmentIncome', 'Currently_Employed__c', e.target.value)}
            className="transition-all focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="Employer__c" className="text-sm font-medium">Employer</Label>
            <Input
              id="Employer__c"
              value={formData.employmentIncome.Employer__c}
              onChange={(e) => handleInputChange('employmentIncome', 'Employer__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="Job_Title__c" className="text-sm font-medium">Job Title</Label>
            <Input
              id="Job_Title__c"
              value={formData.employmentIncome.Job_Title__c}
              onChange={(e) => handleInputChange('employmentIncome', 'Job_Title__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="Work_Phone__c" className="text-sm font-medium">Work Phone</Label>
          <Input
            id="Work_Phone__c"
            value={formData.employmentIncome.Work_Phone__c}
            onChange={(e) => handleInputChange('employmentIncome', 'Work_Phone__c', e.target.value)}
            className="transition-all focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="Employment_Goals__c" className="text-sm font-medium">Employment Goals</Label>
          <Input
            id="Employment_Goals__c"
            value={formData.employmentIncome.Employment_Goals__c}
            onChange={(e) => handleInputChange('employmentIncome', 'Employment_Goals__c', e.target.value)}
            className="transition-all focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="Employment_Comments__c" className="text-sm font-medium">Employment Comments</Label>
          <Textarea
            id="Employment_Comments__c"
            value={formData.employmentIncome.Employment_Comments__c}
            onChange={(e) => handleInputChange('employmentIncome', 'Employment_Comments__c', e.target.value)}
            rows={3}
            className="transition-all focus:ring-2 focus:ring-purple-500"
          />
        </div>
      </div>

      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium text-sm text-gray-700">Employment History</h4>
        <div className="space-y-2">
          <Label htmlFor="Employment_Status_on_Entry__c" className="text-sm font-medium">Employment Status on Entry</Label>
          <Input
            id="Employment_Status_on_Entry__c"
            value={formData.employmentIncome.Employment_Status_on_Entry__c}
            onChange={(e) => handleInputChange('employmentIncome', 'Employment_Status_on_Entry__c', e.target.value)}
            className="transition-all focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="Employment_Start__c" className="text-sm font-medium">Employment Start</Label>
            <Input
              id="Employment_Start__c"
              type="date"
              value={formData.employmentIncome.Employment_Start__c}
              onChange={(e) => handleInputChange('employmentIncome', 'Employment_Start__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="Employment_End__c" className="text-sm font-medium">Employment End</Label>
            <Input
              id="Employment_End__c"
              type="date"
              value={formData.employmentIncome.Employment_End__c}
              onChange={(e) => handleInputChange('employmentIncome', 'Employment_End__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="Reason_for_Leaving__c" className="text-sm font-medium">Reason for Leaving</Label>
          <Input
            id="Reason_for_Leaving__c"
            value={formData.employmentIncome.Reason_for_Leaving__c}
            onChange={(e) => handleInputChange('employmentIncome', 'Reason_for_Leaving__c', e.target.value)}
            className="transition-all focus:ring-2 focus:ring-purple-500"
          />
        </div>
      </div>

      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium text-sm text-gray-700">Income Sources</h4>
        <div className="space-y-2">
          <Label htmlFor="Income_Source__c" className="text-sm font-medium">Income Source</Label>
          <Input
            id="Income_Source__c"
            value={formData.employmentIncome.Income_Source__c}
            onChange={(e) => handleInputChange('employmentIncome', 'Income_Source__c', e.target.value)}
            className="transition-all focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="PRIV__c" className="text-sm font-medium">PRIV</Label>
            <Input
              id="PRIV__c"
              type="number"
              value={formData.employmentIncome.PRIV__c}
              onChange={(e) => handleInputChange('employmentIncome', 'PRIV__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="SSDI__c" className="text-sm font-medium">SSDI</Label>
            <Input
              id="SSDI__c"
              type="number"
              value={formData.employmentIncome.SSDI__c}
              onChange={(e) => handleInputChange('employmentIncome', 'SSDI__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="SSI__c" className="text-sm font-medium">SSI</Label>
            <Input
              id="SSI__c"
              type="number"
              value={formData.employmentIncome.SSI__c}
              onChange={(e) => handleInputChange('employmentIncome', 'SSI__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="VA__c" className="text-sm font-medium">VA</Label>
            <Input
              id="VA__c"
              type="number"
              value={formData.employmentIncome.VA__c}
              onChange={(e) => handleInputChange('employmentIncome', 'VA__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="SSA__c" className="text-sm font-medium">SSA</Label>
            <Input
              id="SSA__c"
              type="number"
              value={formData.employmentIncome.SSA__c}
              onChange={(e) => handleInputChange('employmentIncome', 'SSA__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="Other__c" className="text-sm font-medium">Other</Label>
            <Input
              id="Other__c"
              value={formData.employmentIncome.Other__c}
              onChange={(e) => handleInputChange('employmentIncome', 'Other__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="Other_Type__c" className="text-sm font-medium">Other Type</Label>
            <Input
              id="Other_Type__c"
              value={formData.employmentIncome.Other_Type__c}
              onChange={(e) => handleInputChange('employmentIncome', 'Other_Type__c', e.target.value)}
              className="transition-all focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="Income_Level__c" className="text-sm font-medium">Income Level</Label>
          <Input
            id="Income_Level__c"
            value={formData.employmentIncome.Income_Level__c}
            onChange={(e) => handleInputChange('employmentIncome', 'Income_Level__c', e.target.value)}
            className="transition-all focus:ring-2 focus:ring-purple-500"
          />
        </div>
      </div>
    </div>
  );

  const renderEducation = () => (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-lg border border-amber-100">
        <div className="flex items-center gap-2 mb-2">
          <GraduationCap className="w-5 h-5 text-amber-600" />
          <h3 className="font-semibold text-amber-900">Education</h3>
        </div>
        <p className="text-sm text-amber-700">Please provide information about educational background and goals</p>
      </div>

      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium text-sm text-gray-700">Educational Background</h4>
        <div className="space-y-2">
          <Label htmlFor="Highest_Education_Completed__c" className="text-sm font-medium">Highest Education Completed</Label>
          <Input
            id="Highest_Education_Completed__c"
            value={formData.education.Highest_Education_Completed__c}
            onChange={(e) => handleInputChange('education', 'Highest_Education_Completed__c', e.target.value)}
            className="transition-all focus:ring-2 focus:ring-amber-500"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="Education__c" className="text-sm font-medium">Education</Label>
          <Input
            id="Education__c"
            value={formData.education.Education__c}
            onChange={(e) => handleInputChange('education', 'Education__c', e.target.value)}
            className="transition-all focus:ring-2 focus:ring-amber-500"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="Current_Education_Goals__c" className="text-sm font-medium">Current Education Goals</Label>
          <Input
            id="Current_Education_Goals__c"
            value={formData.education.Current_Education_Goals__c}
            onChange={(e) => handleInputChange('education', 'Current_Education_Goals__c', e.target.value)}
            className="transition-all focus:ring-2 focus:ring-amber-500"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="Educational_Comments__c" className="text-sm font-medium">Educational Comments</Label>
          <Textarea
            id="Educational_Comments__c"
            value={formData.education.Educational_Comments__c}
            onChange={(e) => handleInputChange('education', 'Educational_Comments__c', e.target.value)}
            rows={3}
            className="transition-all focus:ring-2 focus:ring-amber-500"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="Communication_Notes__c" className="text-sm font-medium">Communication Notes</Label>
          <Textarea
            id="Communication_Notes__c"
            value={formData.education.Communication_Notes__c}
            onChange={(e) => handleInputChange('education', 'Communication_Notes__c', e.target.value)}
            rows={3}
            className="transition-all focus:ring-2 focus:ring-amber-500"
          />
        </div>
      </div>
    </div>
  );

  const renderGoals = () => {
    const goalTypeOptions = [
      'Communication',
      'Community-Based Living',
      'Community/Social Participation',
      'Educational',
      'Information Access/Technology',
      'Mobility/Transportation',
      'Other',
      'Personal Resource Management',
      'Relocation from a Nursing Home or Institution to Community-Based Living',
      'Self-Advocacy/Self-Empowerment',
      'Self-Care',
      'Vocational',
      'A-SELF-ADVOCACY/SELF-EMPOWERMENT',
      'B-COMMUNICATION',
      'C-MOBILITY/TRANSPORTATION',
      'D-COMMUNITY BASED LIVING',
      'E-EDUCATIONAL',
      'F-VOCATIONAL',
      'G-SELF-CARE',
      'H-INFORMATION ACCESS/TECHNOLOGY',
      'I-PERSONAL RESOURCE MANAGEMENT',
      'J-RELOCATION FROM NH OR INST TO COMMUNITY',
      'K-COMMUNITY/SOCIAL PARTICIPATION'
    ];

    const addGoal = () => {
      const newGoal = {
        id: Date.now(),
        Staff__c: '',
        County__c: '',
        Area_of_Access__c: '',
        Goal_Type__c: '',
        Situation__c: '',
        Need__c: '',
        Long_Term_Goal__c: '',
        Short_Term_Goal__c: '',
        Method__c: '',
        Begun__c: '',
        Target__c: '',
        Accomplished__c: '',
        Cancel__c: '',
        Service_Provider_Followup__c: '',
        actionSteps: []
      };
      
      setFormData(prev => ({
        ...prev,
        goals: [...prev.goals, newGoal]
      }));
    };

    const removeGoal = (goalId) => {
      setFormData(prev => ({
        ...prev,
        goals: prev.goals.filter(goal => goal.id !== goalId)
      }));
    };

    const updateGoal = (goalId, field, value) => {
      setFormData(prev => ({
        ...prev,
        goals: prev.goals.map(goal => 
          goal.id === goalId ? { ...goal, [field]: value } : goal
        )
      }));
    };

    const addActionStep = (goalId) => {
      const newActionStep = {
        id: Date.now(),
        Action_Description__c: '',
        Action_Start__c: '',
        Action_Completed__c: '',
        Action_Target__c: '',
        Action_Comments__c: '',
        Staff__c: ''
      };

      setFormData(prev => ({
        ...prev,
        goals: prev.goals.map(goal => 
          goal.id === goalId 
            ? { ...goal, actionSteps: [...goal.actionSteps, newActionStep] }
            : goal
        )
      }));
    };

    const removeActionStep = (goalId, actionStepId) => {
      setFormData(prev => ({
        ...prev,
        goals: prev.goals.map(goal => 
          goal.id === goalId 
            ? { ...goal, actionSteps: goal.actionSteps.filter(step => step.id !== actionStepId) }
            : goal
        )
      }));
    };

    const updateActionStep = (goalId, actionStepId, field, value) => {
      setFormData(prev => ({
        ...prev,
        goals: prev.goals.map(goal => 
          goal.id === goalId 
            ? {
                ...goal,
                actionSteps: goal.actionSteps.map(step =>
                  step.id === actionStepId ? { ...step, [field]: value } : step
                )
              }
            : goal
        )
      }));
    };

    return (
      <div className="space-y-6 animate-in fade-in duration-300">
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-lg border border-indigo-100">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-5 h-5 text-indigo-600" />
            <h3 className="font-semibold text-indigo-900">Goals & Action Steps</h3>
          </div>
          <p className="text-sm text-indigo-700">Add and manage goals with their associated action steps</p>
        </div>

        <div className="space-y-6">
          {formData.goals.map((goal, goalIndex) => (
            <div key={goal.id} className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-lg text-gray-900">Goal #{goalIndex + 1}</h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeGoal(goal.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Remove Goal
                </Button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor={`staff_${goal.id}`} className="text-sm font-medium">Staff</Label>
                    <Input
                      id={`staff_${goal.id}`}
                      value={goal.Staff__c}
                      onChange={(e) => updateGoal(goal.id, 'Staff__c', e.target.value)}
                      className="transition-all focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`county_${goal.id}`} className="text-sm font-medium">County</Label>
                    <Input
                      id={`county_${goal.id}`}
                      value={goal.County__c}
                      onChange={(e) => updateGoal(goal.id, 'County__c', e.target.value)}
                      className="transition-all focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor={`area_${goal.id}`} className="text-sm font-medium">Area of Access</Label>
                    <Input
                      id={`area_${goal.id}`}
                      value={goal.Area_of_Access__c}
                      onChange={(e) => updateGoal(goal.id, 'Area_of_Access__c', e.target.value)}
                      className="transition-all focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`goaltype_${goal.id}`} className="text-sm font-medium">Goal Type</Label>
                    <Select
                      value={goal.Goal_Type__c}
                      onValueChange={(value) => updateGoal(goal.id, 'Goal_Type__c', value)}
                    >
                      <SelectTrigger className="transition-all focus:ring-2 focus:ring-indigo-500">
                        <SelectValue placeholder="Select goal type" />
                      </SelectTrigger>
                      <SelectContent>
                        {goalTypeOptions.map(option => (
                          <SelectItem key={option} value={option}>{option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`situation_${goal.id}`} className="text-sm font-medium">Situation</Label>
                  <Textarea
                    id={`situation_${goal.id}`}
                    value={goal.Situation__c}
                    onChange={(e) => updateGoal(goal.id, 'Situation__c', e.target.value)}
                    rows={2}
                    className="transition-all focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`need_${goal.id}`} className="text-sm font-medium">Need</Label>
                  <Textarea
                    id={`need_${goal.id}`}
                    value={goal.Need__c}
                    onChange={(e) => updateGoal(goal.id, 'Need__c', e.target.value)}
                    rows={2}
                    className="transition-all focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`longterm_${goal.id}`} className="text-sm font-medium">Long Term Goal</Label>
                  <Textarea
                    id={`longterm_${goal.id}`}
                    value={goal.Long_Term_Goal__c}
                    onChange={(e) => updateGoal(goal.id, 'Long_Term_Goal__c', e.target.value)}
                    rows={2}
                    className="transition-all focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`shortterm_${goal.id}`} className="text-sm font-medium">Short Term Goal</Label>
                  <Textarea
                    id={`shortterm_${goal.id}`}
                    value={goal.Short_Term_Goal__c}
                    onChange={(e) => updateGoal(goal.id, 'Short_Term_Goal__c', e.target.value)}
                    rows={2}
                    className="transition-all focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`method_${goal.id}`} className="text-sm font-medium">Method</Label>
                  <Textarea
                    id={`method_${goal.id}`}
                    value={goal.Method__c}
                    onChange={(e) => updateGoal(goal.id, 'Method__c', e.target.value)}
                    rows={2}
                    className="transition-all focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor={`begun_${goal.id}`} className="text-sm font-medium">Begun</Label>
                    <Input
                      id={`begun_${goal.id}`}
                      type="date"
                      value={goal.Begun__c}
                      onChange={(e) => updateGoal(goal.id, 'Begun__c', e.target.value)}
                      className="transition-all focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`target_${goal.id}`} className="text-sm font-medium">Target</Label>
                    <Input
                      id={`target_${goal.id}`}
                      type="date"
                      value={goal.Target__c}
                      onChange={(e) => updateGoal(goal.id, 'Target__c', e.target.value)}
                      className="transition-all focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor={`accomplished_${goal.id}`} className="text-sm font-medium">Accomplished</Label>
                    <Select
                      value={goal.Accomplished__c}
                      onValueChange={(value) => updateGoal(goal.id, 'Accomplished__c', value)}
                    >
                      <SelectTrigger className="transition-all focus:ring-2 focus:ring-indigo-500">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`cancel_${goal.id}`} className="text-sm font-medium">Cancel</Label>
                    <Input
                      id={`cancel_${goal.id}`}
                      value={goal.Cancel__c}
                      onChange={(e) => updateGoal(goal.id, 'Cancel__c', e.target.value)}
                      className="transition-all focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`followup_${goal.id}`} className="text-sm font-medium">Service Provider Followup</Label>
                  <Textarea
                    id={`followup_${goal.id}`}
                    value={goal.Service_Provider_Followup__c}
                    onChange={(e) => updateGoal(goal.id, 'Service_Provider_Followup__c', e.target.value)}
                    rows={2}
                    className="transition-all focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* Action Steps Section */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="font-semibold text-md text-gray-800">Action Steps</h5>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addActionStep(goal.id)}
                      className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Action Step
                    </Button>
                  </div>

                  {goal.actionSteps.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No action steps added yet.</p>
                  ) : (
                    <div className="space-y-4">
                      {goal.actionSteps.map((actionStep, actionIndex) => (
                        <div key={actionStep.id} className="border border-gray-200 rounded-md p-4 bg-gray-50">
                          <div className="flex items-center justify-between mb-3">
                            <h6 className="font-medium text-sm text-gray-700">Action Step #{actionIndex + 1}</h6>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeActionStep(goal.id, actionStep.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>

                          <div className="space-y-3">
                            <div className="space-y-2">
                              <Label htmlFor={`action_desc_${actionStep.id}`} className="text-xs font-medium">Action Description</Label>
                              <Textarea
                                id={`action_desc_${actionStep.id}`}
                                value={actionStep.Action_Description__c}
                                onChange={(e) => updateActionStep(goal.id, actionStep.id, 'Action_Description__c', e.target.value)}
                                rows={2}
                                className="transition-all focus:ring-2 focus:ring-indigo-500 text-sm"
                              />
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                              <div className="space-y-2">
                                <Label htmlFor={`action_start_${actionStep.id}`} className="text-xs font-medium">Action Start</Label>
                                <Input
                                  id={`action_start_${actionStep.id}`}
                                  type="date"
                                  value={actionStep.Action_Start__c}
                                  onChange={(e) => updateActionStep(goal.id, actionStep.id, 'Action_Start__c', e.target.value)}
                                  className="transition-all focus:ring-2 focus:ring-indigo-500 text-sm"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor={`action_target_${actionStep.id}`} className="text-xs font-medium">Action Target</Label>
                                <Input
                                  id={`action_target_${actionStep.id}`}
                                  type="date"
                                  value={actionStep.Action_Target__c}
                                  onChange={(e) => updateActionStep(goal.id, actionStep.id, 'Action_Target__c', e.target.value)}
                                  className="transition-all focus:ring-2 focus:ring-indigo-500 text-sm"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor={`action_completed_${actionStep.id}`} className="text-xs font-medium">Action Completed</Label>
                                <Input
                                  id={`action_completed_${actionStep.id}`}
                                  type="date"
                                  value={actionStep.Action_Completed__c}
                                  onChange={(e) => updateActionStep(goal.id, actionStep.id, 'Action_Completed__c', e.target.value)}
                                  className="transition-all focus:ring-2 focus:ring-indigo-500 text-sm"
                                />
                              </div>
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor={`action_staff_${actionStep.id}`} className="text-xs font-medium">Staff</Label>
                              <Input
                                id={`action_staff_${actionStep.id}`}
                                value={actionStep.Staff__c}
                                onChange={(e) => updateActionStep(goal.id, actionStep.id, 'Staff__c', e.target.value)}
                                className="transition-all focus:ring-2 focus:ring-indigo-500 text-sm"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor={`action_comments_${actionStep.id}`} className="text-xs font-medium">Action Comments</Label>
                              <Textarea
                                id={`action_comments_${actionStep.id}`}
                                value={actionStep.Action_Comments__c}
                                onChange={(e) => updateActionStep(goal.id, actionStep.id, 'Action_Comments__c', e.target.value)}
                                rows={2}
                                className="transition-all focus:ring-2 focus:ring-indigo-500 text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {formData.goals.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
              <Target className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-4">No goals added yet.</p>
              <Button
                onClick={addGoal}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Goal
              </Button>
            </div>
          ) : (
            <Button
              onClick={addGoal}
              variant="outline"
              className="w-full border-2 border-dashed border-indigo-300 text-indigo-600 hover:bg-indigo-50"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Another Goal
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderReview = () => (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-100">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-blue-900">Review Information</h3>
        </div>
        <p className="text-sm text-blue-700">Please review all entered information before submitting</p>
      </div>

      {/* Personal Information Section */}
      <div className="space-y-4 pt-4 border-t">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-blue-600" />
          <h4 className="font-semibold text-sm text-gray-900">Personal Information</h4>
        </div>
        <div className="grid grid-cols-2 gap-4 pl-6">
          {formData.personalInfo.FirstName && (
            <div><span className="text-xs text-gray-600">First Name:</span> <span className="text-sm font-medium">{formData.personalInfo.FirstName}</span></div>
          )}
          {formData.personalInfo.LastName && (
            <div><span className="text-xs text-gray-600">Last Name:</span> <span className="text-sm font-medium">{formData.personalInfo.LastName}</span></div>
          )}
          {formData.personalInfo.MiddleName__c && (
            <div><span className="text-xs text-gray-600">Middle Name:</span> <span className="text-sm font-medium">{formData.personalInfo.MiddleName__c}</span></div>
          )}
          {formData.personalInfo.Suffix__c && (
            <div><span className="text-xs text-gray-600">Suffix:</span> <span className="text-sm font-medium">{formData.personalInfo.Suffix__c}</span></div>
          )}
          {formData.personalInfo.Birthdate && (
            <div><span className="text-xs text-gray-600">Birth Date:</span> <span className="text-sm font-medium">{formData.personalInfo.Birthdate}</span></div>
          )}
          {formData.personalInfo.SSN__c && (
            <div><span className="text-xs text-gray-600">SSN:</span> <span className="text-sm font-medium">***-**-****</span></div>
          )}
          {formData.personalInfo.Preferred_Pronouns__c && (
            <div><span className="text-xs text-gray-600">Preferred Pronouns:</span> <span className="text-sm font-medium">{formData.personalInfo.Preferred_Pronouns__c}</span></div>
          )}
          {formData.personalInfo.Gender__c && (
            <div><span className="text-xs text-gray-600">Gender:</span> <span className="text-sm font-medium">{formData.personalInfo.Gender__c}</span></div>
          )}
          {formData.personalInfo.Race__c && (
            <div><span className="text-xs text-gray-600">Race:</span> <span className="text-sm font-medium">{formData.personalInfo.Race__c}</span></div>
          )}
        </div>
      </div>

      {/* Contact Details Section */}
      <div className="space-y-4 pt-4 border-t">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-green-600" />
          <h4 className="font-semibold text-sm text-gray-900">Contact Details</h4>
        </div>
        <div className="grid grid-cols-2 gap-4 pl-6">
          {formData.contactDetails.MailingStreet && (
            <div className="col-span-2"><span className="text-xs text-gray-600">Street Address:</span> <span className="text-sm font-medium">{formData.contactDetails.MailingStreet}</span></div>
          )}
          {formData.contactDetails.MailingStreet2 && (
            <div className="col-span-2"><span className="text-xs text-gray-600">Street Address 2:</span> <span className="text-sm font-medium">{formData.contactDetails.MailingStreet2}</span></div>
          )}
          {formData.contactDetails.MailingCity && (
            <div><span className="text-xs text-gray-600">City:</span> <span className="text-sm font-medium">{formData.contactDetails.MailingCity}</span></div>
          )}
          {formData.contactDetails.MailingState && (
            <div><span className="text-xs text-gray-600">State:</span> <span className="text-sm font-medium">{formData.contactDetails.MailingState}</span></div>
          )}
          {formData.contactDetails.MailingPostalCode && (
            <div><span className="text-xs text-gray-600">Postal Code:</span> <span className="text-sm font-medium">{formData.contactDetails.MailingPostalCode}</span></div>
          )}
          {formData.contactDetails.County__c && (
            <div><span className="text-xs text-gray-600">County:</span> <span className="text-sm font-medium">{formData.contactDetails.County__c}</span></div>
          )}
          {formData.contactDetails.MobilePhone && (
            <div><span className="text-xs text-gray-600">Mobile Phone:</span> <span className="text-sm font-medium">{formData.contactDetails.MobilePhone}</span></div>
          )}
          {formData.contactDetails.HomePhone && (
            <div><span className="text-xs text-gray-600">Home Phone:</span> <span className="text-sm font-medium">{formData.contactDetails.HomePhone}</span></div>
          )}
          {formData.contactDetails.Phone && (
            <div><span className="text-xs text-gray-600">Work Phone:</span> <span className="text-sm font-medium">{formData.contactDetails.Phone}</span></div>
          )}
          {formData.contactDetails.Email && (
            <div><span className="text-xs text-gray-600">Email:</span> <span className="text-sm font-medium">{formData.contactDetails.Email}</span></div>
          )}
          {formData.contactDetails.Text_Messages__c && (
            <div><span className="text-xs text-gray-600">Text Messages:</span> <span className="text-sm font-medium">Yes</span></div>
          )}
        </div>
      </div>

      {/* Demographics Section */}
      <div className="space-y-4 pt-4 border-t">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-purple-600" />
          <h4 className="font-semibold text-sm text-gray-900">Demographics</h4>
        </div>
        <div className="grid grid-cols-2 gap-4 pl-6">
          {formData.demographics.Consumer_Type__c && (
            <div><span className="text-xs text-gray-600">Consumer Type:</span> <span className="text-sm font-medium">{formData.demographics.Consumer_Type__c}</span></div>
          )}
          {formData.demographics.Veteran_Status__c && (
            <div><span className="text-xs text-gray-600">Veteran Status:</span> <span className="text-sm font-medium">{formData.demographics.Veteran_Status__c}</span></div>
          )}
          {formData.demographics.Registered_Voter__c && (
            <div><span className="text-xs text-gray-600">Registered Voter:</span> <span className="text-sm font-medium">Yes</span></div>
          )}
          {formData.demographics.Emergency_Plan__c && (
            <div><span className="text-xs text-gray-600">Emergency Plan:</span> <span className="text-sm font-medium">Yes</span></div>
          )}
          {formData.demographics.Identification__c && (
            <div><span className="text-xs text-gray-600">Identification:</span> <span className="text-sm font-medium">{formData.demographics.Identification__c}</span></div>
          )}
        </div>
      </div>

      {/* Health Information Section */}
      <div className="space-y-4 pt-4 border-t">
        <div className="flex items-center gap-2">
          <Heart className="w-4 h-4 text-red-600" />
          <h4 className="font-semibold text-sm text-gray-900">Health Information</h4>
        </div>
        <div className="grid grid-cols-2 gap-4 pl-6">
          {formData.healthInfo.Primary_Disability_Category__c && (
            <div><span className="text-xs text-gray-600">Primary Disability Category:</span> <span className="text-sm font-medium">{formData.healthInfo.Primary_Disability_Category__c}</span></div>
          )}
          {formData.healthInfo.Primary_Disability__c && (
            <div><span className="text-xs text-gray-600">Primary Disability:</span> <span className="text-sm font-medium">{formData.healthInfo.Primary_Disability__c}</span></div>
          )}
          {formData.healthInfo.Primary_Disability_Text__c && (
            <div className="col-span-2"><span className="text-xs text-gray-600">Primary Disability Description:</span> <span className="text-sm font-medium">{formData.healthInfo.Primary_Disability_Text__c}</span></div>
          )}
          {formData.healthInfo.Secondary_Disability_Category__c && (
            <div><span className="text-xs text-gray-600">Secondary Disability Category:</span> <span className="text-sm font-medium">{formData.healthInfo.Secondary_Disability_Category__c}</span></div>
          )}
          {formData.healthInfo.Secondary_Disability__c && (
            <div><span className="text-xs text-gray-600">Secondary Disability:</span> <span className="text-sm font-medium">{formData.healthInfo.Secondary_Disability__c}</span></div>
          )}
          {formData.healthInfo.Secondary_Disability_Text__c && (
            <div className="col-span-2"><span className="text-xs text-gray-600">Secondary Disability Description:</span> <span className="text-sm font-medium">{formData.healthInfo.Secondary_Disability_Text__c}</span></div>
          )}
          {formData.healthInfo.Communications_Needs__c && (
            <div><span className="text-xs text-gray-600">Communications Needs:</span> <span className="text-sm font-medium">{formData.healthInfo.Communications_Needs__c}</span></div>
          )}
          {formData.healthInfo.Health_Insurance_Primary__c && (
            <div><span className="text-xs text-gray-600">Primary Insurance:</span> <span className="text-sm font-medium">{formData.healthInfo.Health_Insurance_Primary__c}</span></div>
          )}
          {formData.healthInfo.Health_Insurance_Secondary__c && (
            <div><span className="text-xs text-gray-600">Secondary Insurance:</span> <span className="text-sm font-medium">{formData.healthInfo.Health_Insurance_Secondary__c}</span></div>
          )}
          {formData.healthInfo.Insurance_Plans__c && (
            <div><span className="text-xs text-gray-600">Insurance Plans:</span> <span className="text-sm font-medium">{formData.healthInfo.Insurance_Plans__c}</span></div>
          )}
          {formData.healthInfo.Medicaid_Plan__c && (
            <div><span className="text-xs text-gray-600">Medicaid Plan:</span> <span className="text-sm font-medium">{formData.healthInfo.Medicaid_Plan__c}</span></div>
          )}
        </div>
      </div>

      {/* Living Situation Section */}
      <div className="space-y-4 pt-4 border-t">
        <div className="flex items-center gap-2">
          <Home className="w-4 h-4 text-green-600" />
          <h4 className="font-semibold text-sm text-gray-900">Living Situation</h4>
        </div>
        <div className="grid grid-cols-2 gap-4 pl-6">
          {formData.livingSituation.Number_in_Household__c && (
            <div><span className="text-xs text-gray-600">Number in Household:</span> <span className="text-sm font-medium">{formData.livingSituation.Number_in_Household__c}</span></div>
          )}
          {formData.livingSituation.Head_of_Household__c && (
            <div><span className="text-xs text-gray-600">Head of Household:</span> <span className="text-sm font-medium">{formData.livingSituation.Head_of_Household__c}</span></div>
          )}
          {formData.livingSituation.Marital_Status__c && (
            <div><span className="text-xs text-gray-600">Marital Status:</span> <span className="text-sm font-medium">{formData.livingSituation.Marital_Status__c}</span></div>
          )}
          {formData.livingSituation.Number_of_Children__c && (
            <div><span className="text-xs text-gray-600">Number of Children:</span> <span className="text-sm font-medium">{formData.livingSituation.Number_of_Children__c}</span></div>
          )}
          {formData.livingSituation.Emergency_Contact_Information__c && (
            <div className="col-span-2"><span className="text-xs text-gray-600">Emergency Contact:</span> <span className="text-sm font-medium">{formData.livingSituation.Emergency_Contact_Information__c}</span></div>
          )}
          {formData.livingSituation.Living_Situation_on_Entry__c && (
            <div><span className="text-xs text-gray-600">Living Situation on Entry:</span> <span className="text-sm font-medium">{formData.livingSituation.Living_Situation_on_Entry__c}</span></div>
          )}
          {formData.livingSituation.Current_Living_Situation__c && (
            <div><span className="text-xs text-gray-600">Current Living Situation:</span> <span className="text-sm font-medium">{formData.livingSituation.Current_Living_Situation__c}</span></div>
          )}
          {formData.livingSituation.Living_Situation_Accessible__c && (
            <div><span className="text-xs text-gray-600">Living Situation Accessible:</span> <span className="text-sm font-medium">{formData.livingSituation.Living_Situation_Accessible__c}</span></div>
          )}
          {formData.livingSituation.Living_Situation_Comments__c && (
            <div className="col-span-2"><span className="text-xs text-gray-600">Living Situation Comments:</span> <span className="text-sm font-medium">{formData.livingSituation.Living_Situation_Comments__c}</span></div>
          )}
          {formData.livingSituation.Relocation_Successful_from_Institution__c && (
            <div><span className="text-xs text-gray-600">Relocation from Institution:</span> <span className="text-sm font-medium">{formData.livingSituation.Relocation_Successful_from_Institution__c}</span></div>
          )}
          {formData.livingSituation.Relocation_Prevented__c && (
            <div><span className="text-xs text-gray-600">Relocation Prevented:</span> <span className="text-sm font-medium">{formData.livingSituation.Relocation_Prevented__c}</span></div>
          )}
          {formData.livingSituation.Transportation_Method__c && (
            <div><span className="text-xs text-gray-600">Transportation Method:</span> <span className="text-sm font-medium">{formData.livingSituation.Transportation_Method__c}</span></div>
          )}
          {formData.livingSituation.Transportation_Adequate__c && (
            <div><span className="text-xs text-gray-600">Transportation Adequate:</span> <span className="text-sm font-medium">{formData.livingSituation.Transportation_Adequate__c}</span></div>
          )}
          {formData.livingSituation.Transportation_Notes__c && (
            <div className="col-span-2"><span className="text-xs text-gray-600">Transportation Notes:</span> <span className="text-sm font-medium">{formData.livingSituation.Transportation_Notes__c}</span></div>
          )}
          {formData.livingSituation.Communication_Style__c && (
            <div><span className="text-xs text-gray-600">Communication Style:</span> <span className="text-sm font-medium">{formData.livingSituation.Communication_Style__c}</span></div>
          )}
        </div>
      </div>

      {/* Employment & Income Section */}
      <div className="space-y-4 pt-4 border-t">
        <div className="flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-purple-600" />
          <h4 className="font-semibold text-sm text-gray-900">Employment & Income</h4>
        </div>
        <div className="grid grid-cols-2 gap-4 pl-6">
          {formData.employmentIncome.Currently_Employed__c && (
            <div><span className="text-xs text-gray-600">Currently Employed:</span> <span className="text-sm font-medium">{formData.employmentIncome.Currently_Employed__c}</span></div>
          )}
          {formData.employmentIncome.Employer__c && (
            <div><span className="text-xs text-gray-600">Employer:</span> <span className="text-sm font-medium">{formData.employmentIncome.Employer__c}</span></div>
          )}
          {formData.employmentIncome.Job_Title__c && (
            <div><span className="text-xs text-gray-600">Job Title:</span> <span className="text-sm font-medium">{formData.employmentIncome.Job_Title__c}</span></div>
          )}
          {formData.employmentIncome.Work_Phone__c && (
            <div><span className="text-xs text-gray-600">Work Phone:</span> <span className="text-sm font-medium">{formData.employmentIncome.Work_Phone__c}</span></div>
          )}
          {formData.employmentIncome.Employment_Goals__c && (
            <div className="col-span-2"><span className="text-xs text-gray-600">Employment Goals:</span> <span className="text-sm font-medium">{formData.employmentIncome.Employment_Goals__c}</span></div>
          )}
          {formData.employmentIncome.Employment_Comments__c && (
            <div className="col-span-2"><span className="text-xs text-gray-600">Employment Comments:</span> <span className="text-sm font-medium">{formData.employmentIncome.Employment_Comments__c}</span></div>
          )}
          {formData.employmentIncome.Employment_Status_on_Entry__c && (
            <div><span className="text-xs text-gray-600">Employment Status on Entry:</span> <span className="text-sm font-medium">{formData.employmentIncome.Employment_Status_on_Entry__c}</span></div>
          )}
          {formData.employmentIncome.Employment_Start__c && (
            <div><span className="text-xs text-gray-600">Employment Start:</span> <span className="text-sm font-medium">{formData.employmentIncome.Employment_Start__c}</span></div>
          )}
          {formData.employmentIncome.Employment_End__c && (
            <div><span className="text-xs text-gray-600">Employment End:</span> <span className="text-sm font-medium">{formData.employmentIncome.Employment_End__c}</span></div>
          )}
          {formData.employmentIncome.Reason_for_Leaving__c && (
            <div className="col-span-2"><span className="text-xs text-gray-600">Reason for Leaving:</span> <span className="text-sm font-medium">{formData.employmentIncome.Reason_for_Leaving__c}</span></div>
          )}
          {formData.employmentIncome.Income_Source__c && (
            <div><span className="text-xs text-gray-600">Income Source:</span> <span className="text-sm font-medium">{formData.employmentIncome.Income_Source__c}</span></div>
          )}
          {formData.employmentIncome.PRIV__c && (
            <div><span className="text-xs text-gray-600">PRIV:</span> <span className="text-sm font-medium">${formData.employmentIncome.PRIV__c}</span></div>
          )}
          {formData.employmentIncome.SSDI__c && (
            <div><span className="text-xs text-gray-600">SSDI:</span> <span className="text-sm font-medium">${formData.employmentIncome.SSDI__c}</span></div>
          )}
          {formData.employmentIncome.SSI__c && (
            <div><span className="text-xs text-gray-600">SSI:</span> <span className="text-sm font-medium">${formData.employmentIncome.SSI__c}</span></div>
          )}
          {formData.employmentIncome.VA__c && (
            <div><span className="text-xs text-gray-600">VA:</span> <span className="text-sm font-medium">${formData.employmentIncome.VA__c}</span></div>
          )}
          {formData.employmentIncome.SSA__c && (
            <div><span className="text-xs text-gray-600">SSA:</span> <span className="text-sm font-medium">${formData.employmentIncome.SSA__c}</span></div>
          )}
          {formData.employmentIncome.Other__c && (
            <div><span className="text-xs text-gray-600">Other:</span> <span className="text-sm font-medium">{formData.employmentIncome.Other__c}</span></div>
          )}
          {formData.employmentIncome.Other_Type__c && (
            <div><span className="text-xs text-gray-600">Other Type:</span> <span className="text-sm font-medium">{formData.employmentIncome.Other_Type__c}</span></div>
          )}
          {formData.employmentIncome.Income_Level__c && (
            <div><span className="text-xs text-gray-600">Income Level:</span> <span className="text-sm font-medium">{formData.employmentIncome.Income_Level__c}</span></div>
          )}
        </div>
      </div>

      {/* Education Section */}
      <div className="space-y-4 pt-4 border-t">
        <div className="flex items-center gap-2">
          <GraduationCap className="w-4 h-4 text-amber-600" />
          <h4 className="font-semibold text-sm text-gray-900">Education</h4>
        </div>
        <div className="grid grid-cols-2 gap-4 pl-6">
          {formData.education.Highest_Education_Completed__c && (
            <div><span className="text-xs text-gray-600">Highest Education Completed:</span> <span className="text-sm font-medium">{formData.education.Highest_Education_Completed__c}</span></div>
          )}
          {formData.education.Education__c && (
            <div><span className="text-xs text-gray-600">Education:</span> <span className="text-sm font-medium">{formData.education.Education__c}</span></div>
          )}
          {formData.education.Current_Education_Goals__c && (
            <div className="col-span-2"><span className="text-xs text-gray-600">Current Education Goals:</span> <span className="text-sm font-medium">{formData.education.Current_Education_Goals__c}</span></div>
          )}
          {formData.education.Educational_Comments__c && (
            <div className="col-span-2"><span className="text-xs text-gray-600">Educational Comments:</span> <span className="text-sm font-medium">{formData.education.Educational_Comments__c}</span></div>
          )}
          {formData.education.Communication_Notes__c && (
            <div className="col-span-2"><span className="text-xs text-gray-600">Communication Notes:</span> <span className="text-sm font-medium">{formData.education.Communication_Notes__c}</span></div>
          )}
        </div>
      </div>

      {/* Goals Section */}
      {formData.goals && formData.goals.length > 0 && (
        <div className="space-y-4 pt-4 border-t">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-indigo-600" />
            <h4 className="font-semibold text-sm text-gray-900">Goals</h4>
          </div>
          <div className="space-y-6 pl-6">
            {formData.goals.map((goal, index) => (
              <div key={goal.id} className="border-l-4 border-indigo-300 pl-4 space-y-3">
                <h5 className="font-semibold text-sm text-indigo-700">Goal #{index + 1}</h5>
                <div className="grid grid-cols-2 gap-4">
                  {goal.Staff__c && (
                    <div><span className="text-xs text-gray-600">Staff:</span> <span className="text-sm font-medium">{goal.Staff__c}</span></div>
                  )}
                  {goal.County__c && (
                    <div><span className="text-xs text-gray-600">County:</span> <span className="text-sm font-medium">{goal.County__c}</span></div>
                  )}
                  {goal.Area_of_Access__c && (
                    <div><span className="text-xs text-gray-600">Area of Access:</span> <span className="text-sm font-medium">{goal.Area_of_Access__c}</span></div>
                  )}
                  {goal.Goal_Type__c && (
                    <div><span className="text-xs text-gray-600">Goal Type:</span> <span className="text-sm font-medium">{goal.Goal_Type__c}</span></div>
                  )}
                  {goal.Situation__c && (
                    <div className="col-span-2"><span className="text-xs text-gray-600">Situation:</span> <span className="text-sm font-medium">{goal.Situation__c}</span></div>
                  )}
                  {goal.Need__c && (
                    <div className="col-span-2"><span className="text-xs text-gray-600">Need:</span> <span className="text-sm font-medium">{goal.Need__c}</span></div>
                  )}
                  {goal.Long_Term_Goal__c && (
                    <div className="col-span-2"><span className="text-xs text-gray-600">Long Term Goal:</span> <span className="text-sm font-medium">{goal.Long_Term_Goal__c}</span></div>
                  )}
                  {goal.Short_Term_Goal__c && (
                    <div className="col-span-2"><span className="text-xs text-gray-600">Short Term Goal:</span> <span className="text-sm font-medium">{goal.Short_Term_Goal__c}</span></div>
                  )}
                  {goal.Method__c && (
                    <div className="col-span-2"><span className="text-xs text-gray-600">Method:</span> <span className="text-sm font-medium">{goal.Method__c}</span></div>
                  )}
                  {goal.Begun__c && (
                    <div><span className="text-xs text-gray-600">Begun:</span> <span className="text-sm font-medium">{goal.Begun__c}</span></div>
                  )}
                  {goal.Target__c && (
                    <div><span className="text-xs text-gray-600">Target:</span> <span className="text-sm font-medium">{goal.Target__c}</span></div>
                  )}
                  {goal.Accomplished__c && (
                    <div><span className="text-xs text-gray-600">Accomplished:</span> <span className="text-sm font-medium">{goal.Accomplished__c}</span></div>
                  )}
                  {goal.Cancel__c && (
                    <div><span className="text-xs text-gray-600">Cancel:</span> <span className="text-sm font-medium">{goal.Cancel__c}</span></div>
                  )}
                  {goal.Service_Provider_Followup__c && (
                    <div className="col-span-2"><span className="text-xs text-gray-600">Service Provider Followup:</span> <span className="text-sm font-medium">{goal.Service_Provider_Followup__c}</span></div>
                  )}
                </div>

                {/* Action Steps for this Goal */}
                {goal.actionSteps && goal.actionSteps.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <h6 className="text-xs font-semibold text-gray-700 mb-2">Action Steps:</h6>
                    <div className="space-y-3">
                      {goal.actionSteps.map((actionStep, actionIndex) => (
                        <div key={actionStep.id} className="bg-gray-50 p-3 rounded border border-gray-200">
                          <p className="text-xs font-medium text-gray-600 mb-2">Action Step #{actionIndex + 1}</p>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            {actionStep.Action_Description__c && (
                              <div className="col-span-2"><span className="text-gray-600">Description:</span> <span className="font-medium">{actionStep.Action_Description__c}</span></div>
                            )}
                            {actionStep.Action_Start__c && (
                              <div><span className="text-gray-600">Start:</span> <span className="font-medium">{actionStep.Action_Start__c}</span></div>
                            )}
                            {actionStep.Action_Target__c && (
                              <div><span className="text-gray-600">Target:</span> <span className="font-medium">{actionStep.Action_Target__c}</span></div>
                            )}
                            {actionStep.Action_Completed__c && (
                              <div><span className="text-gray-600">Completed:</span> <span className="font-medium">{actionStep.Action_Completed__c}</span></div>
                            )}
                            {actionStep.Staff__c && (
                              <div><span className="text-gray-600">Staff:</span> <span className="font-medium">{actionStep.Staff__c}</span></div>
                            )}
                            {actionStep.Action_Comments__c && (
                              <div className="col-span-2"><span className="text-gray-600">Comments:</span> <span className="font-medium">{actionStep.Action_Comments__c}</span></div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return renderPersonalInfo();
      case 1:
        return renderContactDetails();
      case 2:
        return renderDemographics();
      case 3:
        return renderHealthInfo();
      case 4:
        return renderLivingSituation();
      case 5:
        return renderEmploymentIncome();
      case 6:
        return renderEducation();
      case 7:
        return renderGoals();
      case 8:
        return renderReview();
      default:
        return (
          <div className="text-center py-12 animate-in fade-in duration-300">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              {React.createElement(steps[currentStep].icon, { className: "w-8 h-8 text-gray-400" })}
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">{steps[currentStep].name}</h3>
            <p className="text-gray-500">Content for this section coming soon...</p>
          </div>
        );
    }
  };

  return (
    <Card className="w-full max-w-5xl mx-auto shadow-lg">
      <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardTitle className="text-2xl">Consumer Information Wizard</CardTitle>
        <CardDescription className="text-base">
          Step {currentStep + 1} of {steps.length}: {steps[currentStep].name}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="pt-6">
        {/* Enhanced Progress Bar with Icons */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-6">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isCompleted = index < currentStep;
              const isCurrent = index === currentStep;
              
              return (
                <div key={step.name} className="flex flex-col items-center flex-1 relative">
                  {/* Connector Line */}
                  {index > 0 && (
                    <div
                      className={`absolute top-5 right-1/2 w-full h-1 transition-all duration-300 ${
                        index <= currentStep ? 'bg-blue-500' : 'bg-gray-200'
                      }`}
                      style={{ zIndex: 0 }}
                    />
                  )}
                  
                  {/* Icon Circle */}
                  <div
                    className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isCompleted
                        ? 'bg-blue-500 text-white shadow-lg scale-110'
                        : isCurrent
                        ? 'bg-blue-100 text-blue-600 ring-4 ring-blue-200 shadow-md scale-110'
                        : 'bg-gray-100 text-gray-400'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  
                  {/* Step Label */}
                  <span
                    className={`mt-2 text-xs font-medium text-center transition-all duration-300 ${
                      isCurrent ? 'text-blue-600 font-semibold' : 'text-gray-500'
                    }`}
                  >
                    {step.name.split(' ').slice(0, 2).join(' ')}
                  </span>
                </div>
              );
            })}
          </div>
          
          {/* Progress Percentage */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">
              Progress: {Math.round(((currentStep + 1) / steps.length) * 100)}%
            </span>
            <span className="text-gray-500">
              {currentStep + 1} of {steps.length} completed
            </span>
          </div>
        </div>

        {/* Form Content */}
        <div className="min-h-[500px]">
          {renderCurrentStep()}
        </div>
      </CardContent>
      
      <CardFooter className="flex justify-between border-t bg-gray-50 p-6">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={currentStep === 0}
          className="transition-all hover:bg-gray-100"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={downloadJson}
            className="transition-all hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Data
          </Button>
          
          <Button
            onClick={handleNext}
            disabled={currentStep === steps.length - 1}
            className="bg-blue-600 hover:bg-blue-700 transition-all shadow-md hover:shadow-lg"
          >
            {currentStep === steps.length - 1 ? 'Complete' : 'Next'}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default Consumers;